/**
 * Clarity Tracker — visitor analytics + session-replay-lite.
 * Implements the "Clarity Tracker — Frontend Integration Guide".
 *
 * Public API:
 *   Clarity.track(type, data)          push a custom activity event
 *   Clarity.identify(email, name, phone, meta)
 *   Clarity.formSubmit({ name, selector, fields, was_complete })
 *   Clarity.captureForm(rootOrSelector, { name, fields })
 *                                      snapshot a form/container on confirmed
 *                                      success (pairs with data-clarity-manual)
 *   Clarity.flush()                    force-flush the event buffer
 *   Clarity.spaRouteChange()           call after client-side navigation
 *   Clarity.getSessionUuid() / getPageViewUuid() / getVisitorUuid()
 *
 * Lead forms need no markup: a form whose JS takes over the submit is
 * recorded when its request to a known lead endpoint (LEAD_ENDPOINTS)
 * succeeds, with the fields read from that request's body.
 */
(function () {
  'use strict';

  var API_BASE = 'https://clarity.signage.com/api/tracker';
  var SESSION_TIMEOUT_MS = 30 * 60 * 1000;
  var FLUSH_INTERVAL_MS = 5 * 1000;
  var HEARTBEAT_INTERVAL_MS = 10 * 1000;
  // Time since last user input after which the tab counts as idle — engaged
  // time stops accruing at that boundary. Mirrors GA4's 30 s definition.
  var IDLE_THRESHOLD_MS = 30 * 1000;
  var MAX_BATCH = 500;
  var GEO_TIMEOUT_MS = 2000;
  // Largest limit any lead form on the site enforces (signage-v4: 50 MB).
  var FILE_MAX_BYTES = 50 * 1024 * 1024;
  // MIMEs allowed for uploads. `.ai` and `.eps` files are reported by various
  // browsers as `application/postscript`, `application/illustrator`, or with
  // no MIME at all — accept any of those, and also fall back to an extension
  // whitelist below so a missing/unknown MIME on a valid file isn't rejected.
  var OK_FILE_MIMES = [
    'image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf',
    'application/postscript', 'application/illustrator', 'application/vnd.adobe.illustrator',
    'image/x-eps', 'image/webp', 'image/avif',
  ];
  var OK_FILE_EXTS = ['png', 'jpg', 'jpeg', 'svg', 'pdf', 'ai', 'eps', 'webp', 'avif'];
  var BLOCKED_COUNTRIES = ['PK'];
  // URL paths for which tracking is skipped entirely.
  var BLOCKED_PATH_SUBSTRINGS = ['/products/'];

  function isPathBlocked(path) {
    if (!path) return false;
    for (var i = 0; i < BLOCKED_PATH_SUBSTRINGS.length; i++) {
      if (path.indexOf(BLOCKED_PATH_SUBSTRINGS[i]) !== -1) return true;
    }
    return false;
  }

  // ---------- storage helpers ----------
  function setCookie(name, value, days) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + days * 86400000);
      var attrs = ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
      // Secure is required for SameSite semantics on modern browsers and makes
      // strict-privacy modes more willing to persist the cookie.
      if (location.protocol === 'https:') attrs += ';Secure';
      document.cookie = name + '=' + encodeURIComponent(value) + attrs;
    } catch (_) { }
  }
  function getCookie(name) {
    var m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : null;
  }
  // theme.liquid defines window.safeLocalStorage / window.safeSessionStorage
  // near the top of <head>. Those wrappers never throw and fall back to an
  // in-memory store if the real Storage is blocked (Chrome 3P cookie block,
  // Edge tracking prevention, Safari ITP, Incognito, enterprise policy). Use
  // them here so the tracker degrades the same way the rest of the theme does.
  function _ls() {
    try { return window.safeLocalStorage || window.localStorage || null; } catch (_) { return null; }
  }
  function _ss() {
    try { return window.safeSessionStorage || window.sessionStorage || null; } catch (_) { return null; }
  }
  function lsGet(k) { var s = _ls(); try { return s ? s.getItem(k) : null; } catch (_) { return null; } }
  function lsSet(k, v) { var s = _ls(); try { if (s) s.setItem(k, v); } catch (_) { } }
  function lsRemove(k) { var s = _ls(); try { if (s) s.removeItem(k); } catch (_) { } }

  // Session-scoped storage with a localStorage fallback. When sessionStorage
  // is unavailable we degrade to localStorage so session state still survives
  // a reload. Both are written on set so a later read finds the value in
  // whichever store is available.
  function ssGet(k) {
    var s = _ss();
    try {
      var v = s ? s.getItem(k) : null;
      if (v !== null) return v;
    } catch (_) { }
    return lsGet(k);
  }
  function ssSet(k, v) {
    var s = _ss();
    try { if (s) s.setItem(k, v); } catch (_) { }
    lsSet(k, v);
  }
  function ssRemove(k) {
    var s = _ss();
    try { if (s) s.removeItem(k); } catch (_) { }
    lsRemove(k);
  }

  // ---------- session-id persistence (fault-tolerant fallback chain) ----------
  // The session UUID has to survive a full page reload even when cookies are
  // blocked/disabled (which storage-blocking browser modes routinely do
  // alongside 3P-cookie policies). Chain, in priority order:
  //   1. Cookie              — shared across tabs, survives reload, sent to server automatically
  //   2. sessionStorage/localStorage (via ssGet/ssSet, already degrade to each other)
  //   3. URL query param     — recovers a session id a same-origin page linked with ?csid=,
  //                            useful only when the referring page appended one; best-effort
  //   4. In-memory variable  — last resort; survives only for the life of this page (no reload)
  // Reads check the chain in order and stop at the first hit. Writes fan out
  // to every mechanism so a later read still resolves the session even if
  // whichever store served the last read becomes unavailable.
  var SESSION_ID_KEY = 'clarity_session_uuid';
  var SESSION_URL_PARAM = 'csid';
  // Keep the cookie itself alive longer than the session-idle timeout.
  // Some strict-privacy browser modes drop cookies with very short lifetimes
  // (< 1 hour) as suspected trackers. The real 30-min idle expiry is
  // enforced by the server (CloseInactiveSessions) and by the last-activity
  // timestamp check below — the cookie just needs to survive a page reload.
  var SESSION_ID_TTL_DAYS = 1;
  var memorySessionUuid = null;

  function readSessionIdFromUrl() {
    try {
      var v = new URLSearchParams(location.search).get(SESSION_URL_PARAM);
      return (v && /^[0-9a-f-]{16,64}$/i.test(v)) ? v : null;
    } catch (_) { return null; }
  }

  function readSessionId() {
    return getCookie(SESSION_ID_KEY) ||
      ssGet(SESSION_ID_KEY) ||
      readSessionIdFromUrl() ||
      memorySessionUuid ||
      null;
  }

  function writeSessionId(id) {
    memorySessionUuid = id;
    setCookie(SESSION_ID_KEY, id, SESSION_ID_TTL_DAYS);
    ssSet(SESSION_ID_KEY, id);
  }

  function clearSessionId() {
    memorySessionUuid = null;
    setCookie(SESSION_ID_KEY, '', -1);
    ssRemove(SESSION_ID_KEY);
  }

  // Staleness (and thus session recovery) is judged from this timestamp, so
  // it needs the same fallback chain as the session id itself — otherwise a
  // browser with sessionStorage/localStorage blocked would read a null
  // timestamp, be judged "stale", and discard a perfectly valid cookie session.
  var ACTIVITY_KEY = 'clarity_last_activity_ts';
  var memoryLastActivity = null;

  function readLastActivity() {
    return getCookie(ACTIVITY_KEY) || ssGet(ACTIVITY_KEY) || memoryLastActivity || null;
  }

  function writeLastActivity(v) {
    memoryLastActivity = v;
    setCookie(ACTIVITY_KEY, v, SESSION_ID_TTL_DAYS);
    ssSet(ACTIVITY_KEY, v);
  }

  function uuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      var v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  function nowIso() { return new Date().toISOString(); }

  // ---------- identity ----------
  // Visitor UUID must survive across tabs and reloads. Read from the first
  // available store (cookie → localStorage → sessionStorage); write to every
  // store that works so a later read still resolves it if one gets blocked.
  // Without this fallback, a browser that blocks cookies would mint a new
  // visitor UUID on every reload and the backend would open a fresh session
  // each time.
  function readVisitor() {
    return getCookie('clarity_visitor_uuid') ||
      lsGet('clarity_visitor_uuid') ||
      ssGet('clarity_visitor_uuid') ||
      null;
  }
  function writeVisitor(v) {
    setCookie('clarity_visitor_uuid', v, 730);
    lsSet('clarity_visitor_uuid', v);
    ssSet('clarity_visitor_uuid', v);
  }

  var visitorUuid = readVisitor();
  if (!visitorUuid) visitorUuid = uuid();
  writeVisitor(visitorUuid);

  var sessionUuid = null;
  var pageViewUuid = null;
  var previousPageViewUuid = ssGet('clarity_page_view_uuid') || null;
  var pageEnteredAtMs = Date.now();
  var maxScroll = 0;
  var isBlocked = false;         // hard block (geo) — disables everything permanently
  var pageBlocked = false;       // soft block (per-pageview URL) — flips on SPA nav
  var listenersAttached = false;
  var intervalsStarted = false;

  // Trust the session cookie's own presence as primary. The activity
  // timestamp is a secondary idle guard — if it's missing (async write race
  // on a fast navigation, cleared by a privacy extension, etc.) we still
  // honour a valid session cookie rather than silently minting a new session
  // on every page load.
  sessionUuid = readSessionId();
  if (sessionUuid) {
    var lastActivityRaw = readLastActivity();
    var lastActivityMs = lastActivityRaw ? Date.parse(lastActivityRaw) : 0;
    var sessionIdleTooLong =
      lastActivityMs > 0 &&
      (Date.now() - lastActivityMs) > SESSION_TIMEOUT_MS;
    if (sessionIdleTooLong) {
      clearSessionId();
      sessionUuid = null;
    } else {
      // Rewrite through the full chain so cookies resync if a previous
      // write landed only in sessionStorage / memory.
      writeSessionId(sessionUuid);
    }
  }

  function touch() {
    writeLastActivity(nowIso());
    // Refresh the session cookie's rolling expiry so it doesn't outlive (or
    // die before) the sessionStorage/localStorage copy of the same id.
    if (sessionUuid) setCookie(SESSION_ID_KEY, sessionUuid, SESSION_ID_TTL_DAYS);
  }

  // ---------- event buffer ----------
  var buffer = [];

  function push(type, extra) {
    if (isBlocked || pageBlocked) return;
    if (buffer.length >= MAX_BATCH) buffer.shift();
    var evt = { type: type, occurred_at: nowIso() };
    if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) evt[k] = extra[k];
    buffer.push(evt);
    touch();
  }

  // ---------- HTTP ----------
  // Every request is a keepalive fetch, which (like sendBeacon) survives page
  // unload. navigator.sendBeacon is deliberately NOT used: it always sends
  // credentials, so its JSON body's CORS preflight fails against the API's
  // `Access-Control-Allow-Origin: *` — the browser drops the request while
  // sendBeacon() still returns true, silently losing /form-submit,
  // /identify and /pageview/close. `useBeacon` now only marks unload-time
  // sends, which must not depend on a response.
  // keepalive bodies are capped (64 KB in flight); oversized payloads retry
  // as a normal fetch.
  var KEEPALIVE_MAX_BYTES = 60 * 1024;

  function post(path, body, useBeacon) {
    var url = API_BASE + path;
    var payload = JSON.stringify(body);
    var keepalive = payload.length < KEEPALIVE_MAX_BYTES;

    var send = function (ka) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: payload,
        keepalive: ka,
        credentials: 'omit',
      });
    };

    var p;
    try { p = send(keepalive); }
    catch (e) {
      try { p = keepalive ? send(false) : Promise.reject(e); }
      catch (e2) { p = Promise.reject(e2); }
    }
    var out = p.then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.status === 204 ? null : r.json().catch(function () { return null; });
    });
    // Fire-and-forget sends have no caller to handle a failure.
    return useBeacon ? out.catch(function () { return null; }) : out;
  }

  // ---------- IP-geo (best-effort, bounded by GEO_TIMEOUT_MS) ----------
  function fetchGeoWithTimeout() {
    var timeoutP = new Promise(function (res) { setTimeout(function () { res(null); }, GEO_TIMEOUT_MS); });

    var geoP = fetch('https://ipapi.co/json/', { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || !d.country) return null;
        return {
          country: (d.country || '').toUpperCase() || null,
          region: d.region || null,
          city: d.city || null,
          timezone: d.timezone || null,
        };
      })
      .catch(function () { return null; });

    return Promise.race([geoP, timeoutP]);
  }

  // ---------- session + pageview ----------
  function startSession(geo) {
    var params = new URLSearchParams(location.search);
    var tz = null;
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (_) { }

    var body = {
      visitor_uuid: visitorUuid,
      entry_url: location.href,
      referrer: document.referrer || null,
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_term: params.get('utm_term'),
      utm_content: params.get('utm_content'),
      screen_width: screen.width,
      screen_height: screen.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      language: navigator.language,
      timezone: (geo && geo.timezone) || tz,
    };
    if (geo) {
      body.country = geo.country;
      body.region = geo.region;
      body.city = geo.city;
    }

    return post('/session', body).then(function (r) {
      if (r && r.session_uuid) {
        sessionUuid = r.session_uuid;
        writeSessionId(sessionUuid);
        // Commit the activity timestamp immediately so a fast navigation
        // before touch() fires doesn't leave the next page load with a
        // missing timestamp (which would otherwise nuke this session).
        writeLastActivity(nowIso());
      }
    });
  }

  function openPageView() {
    pageEnteredAtMs = Date.now();
    maxScroll = 0;
    // Reset engagement state for the new pageview. If the user is here and
    // active right now (visible + recent input), start the counter now so
    // we don't lose the first few seconds waiting for the next input event.
    engagedMsAccum = 0;
    lastEngagedTickMs = null;
    if (document.visibilityState === 'visible' &&
      (perfNow() - lastInputTs) < IDLE_THRESHOLD_MS) {
      lastEngagedTickMs = perfNow();
    }
    return post('/pageview', {
      session_uuid: sessionUuid,
      url: location.href,
      url_path: location.pathname,
      url_query: location.search.replace(/^\?/, '') || null,
      referrer_url: document.referrer || null,
      page_title: document.title,
      previous_page_view_uuid: previousPageViewUuid,
      entered_at: new Date(pageEnteredAtMs).toISOString(),
    }).then(function (r) {
      if (r && r.page_view_uuid) {
        pageViewUuid = r.page_view_uuid;
        ssSet('clarity_page_view_uuid', pageViewUuid);
      }
      touch();
    });
  }

  // ---------- flush + heartbeat ----------
  function flush(useBeacon) {
    if (isBlocked || !pageViewUuid || buffer.length === 0) return Promise.resolve();
    var events = buffer.splice(0, MAX_BATCH);
    return post('/events', {
      session_uuid: sessionUuid,
      page_view_uuid: pageViewUuid,
      events: events,
    }, useBeacon).catch(function () {
      // Requeue on failure, keeping buffer bounded.
      for (var i = events.length - 1; i >= 0 && buffer.length < MAX_BATCH; i--) {
        buffer.unshift(events[i]);
      }
    });
  }

  // ---------- engaged-time tracking ----------
  // Local accumulator, in ms, of time the tab was visible AND the user was
  // active (last input within IDLE_THRESHOLD_MS). Server can't compute this
  // — only the browser knows focus + input state. Reset to 0 after each
  // successful send. Mirrors GA4's engagement definition.
  var engagedMsAccum = 0;
  // performance.now() at the moment the timer last started/ticked. `null`
  // means paused (tab hidden or user idle).
  var lastEngagedTickMs = null;
  // performance.now() of the most recent user input event. Initialised at
  // boot — page load implies the user just interacted to get here.
  var lastInputTs = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
  var INPUT_EVENTS = ['mousemove', 'scroll', 'keydown', 'touchstart', 'click'];

  function perfNow() {
    return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
  }

  // Advance the engaged counter up to "now", capping at the idle cutoff so a
  // stuck timer can't inflate the value if this function is called long after
  // the user went idle. Pauses the timer if we've crossed that cutoff.
  function accrueEngagedTime() {
    if (lastEngagedTickMs === null) return;
    var now = perfNow();
    var idleCutoff = lastInputTs + IDLE_THRESHOLD_MS;
    var effectiveNow = now < idleCutoff ? now : idleCutoff;
    if (effectiveNow > lastEngagedTickMs) {
      engagedMsAccum += effectiveNow - lastEngagedTickMs;
    }
    if (now >= idleCutoff) {
      lastEngagedTickMs = null; // idle — pause
    } else {
      lastEngagedTickMs = now;
    }
  }

  function pauseEngaged() {
    accrueEngagedTime();
    lastEngagedTickMs = null;
  }

  // Called from every input event (bound with { passive: true, capture: true }).
  // Refreshes the idle timestamp and, if the tab is visible and the counter
  // is paused, resumes it.
  function onInputEvent() {
    lastInputTs = perfNow();
    if (lastEngagedTickMs === null && document.visibilityState === 'visible' && !isBlocked && !pageBlocked) {
      lastEngagedTickMs = lastInputTs;
    }
  }

  // Snapshot the current delta, reset the accumulator, and return it clamped
  // to the heartbeat interval (a stuck timer must not inflate a single beat).
  function consumeEngagedDelta() {
    accrueEngagedTime();
    var delta = Math.floor(engagedMsAccum);
    engagedMsAccum = 0;
    if (delta < 0) delta = 0;
    if (delta > HEARTBEAT_INTERVAL_MS) delta = HEARTBEAT_INTERVAL_MS;
    return delta;
  }

  function heartbeat() {
    if (isBlocked || !pageViewUuid || document.hidden) return;
    var timeOnPage = Math.floor((Date.now() - pageEnteredAtMs) / 1000);
    var delta = consumeEngagedDelta();
    post('/heartbeat', {
      session_uuid: sessionUuid,
      page_view_uuid: pageViewUuid,
      time_on_page_seconds: timeOnPage,
      max_scroll_depth: maxScroll,
      engaged_ms_delta: delta,
    }).catch(function () { });
  }

  // Beacon-flavoured heartbeat — used from visibilitychange:hidden and
  // pagehide so the last accumulated delta survives page unload. Without
  // this, the tail 10–15 s of every session goes uncounted because a
  // regular fetch is cancelled during unload.
  function heartbeatBeacon() {
    if (isBlocked || !pageViewUuid) return;
    pauseEngaged();
    var delta = consumeEngagedDelta();
    if (delta <= 0) return;
    var timeOnPage = Math.floor((Date.now() - pageEnteredAtMs) / 1000);
    post('/heartbeat', {
      session_uuid: sessionUuid,
      page_view_uuid: pageViewUuid,
      time_on_page_seconds: timeOnPage,
      max_scroll_depth: maxScroll,
      engaged_ms_delta: delta,
    }, true);
  }

  // ---------- listeners ----------
  function getScrollDepth() {
    var h = document.documentElement;
    var denom = h.scrollHeight || 1;
    return Math.min(100, Math.round(((h.scrollTop + window.innerHeight) / denom) * 100));
  }

  function safeClassName(el) {
    var c = el && el.className;
    if (typeof c === 'string') return c;
    if (c && typeof c.baseVal === 'string') return c.baseVal; // SVG
    return null;
  }

  // ---------- image upload interception ----------
  // The quote forms' file inputs upload to the signage.com dashboard API
  // (and, on older forms, Cloudinary / the in-house imagescloud service). We passively listen for their responses
  // and remember the resulting URLs so they can be attached to the form
  // submission as `uploadImagesURL` (and mirrored as a `form_input` event so
  // abandoned-form recovery has them too).
  var UPLOAD_URL_PATTERNS = [
    /dashboard\.signage\.com\/api\/uploads\/image/i,
    /dashboard\.signage\.com\/api\/webhook\/image_(?:validate|upload|post)\//i,
    /api\.cloudinary\.com\/.+\/image\/upload/i,
    /imagescloud(?:v\d+)?\.codeblue\.agency\/api\/image\/upload/i,
  ];
  var uploadedImageUrls = [];

  function isUploadEndpoint(url) {
    if (!url) return false;
    var s = String(url);
    for (var i = 0; i < UPLOAD_URL_PATTERNS.length; i++) {
      if (UPLOAD_URL_PATTERNS[i].test(s)) return true;
    }
    return false;
  }

  function isHttpUrl(v) {
    return typeof v === 'string' && /^https?:\/\//i.test(v);
  }

  // Keys whose string value may be the stored file's location.
  var UPLOAD_URL_KEY_RE = /(url|path|src|link|location|image|file)/i;

  // Absolute http(s) URL as-is; a root-relative path ("/storage/x.png") is
  // resolved against the upload request's origin. Anything else (data: URIs,
  // ids, messages) is rejected.
  function toUploadUrl(v, base) {
    if (typeof v !== 'string') return null;
    v = v.trim();
    if (isHttpUrl(v)) return v;
    if (/^\/[^/]/.test(v) && base) {
      try { return new URL(v, base).href; } catch (_) { }
    }
    return null;
  }

  // Response shapes differ per endpoint and aren't all documented:
  //   Cloudinary            { secure_url, url }
  //   imagescloud           { imageUrls: [...] }
  //   signage.com dashboard { success, url } / { url } / { data: { ... } } / ...
  // So walk the whole body (bounded) and keep http(s) strings — plus
  // root-relative paths under url/path/image-like keys.
  function extractUploadUrls(data, base) {
    var out = [];
    function add(u) { if (u && out.indexOf(u) === -1) out.push(u); }
    function walk(node, keyHint, depth) {
      if (node == null || depth > 4 || out.length >= 20) return;
      if (typeof node === 'string') {
        if (isHttpUrl(node.trim())) add(node.trim());
        else if (keyHint) add(toUploadUrl(node, base));
        return;
      }
      if (typeof node !== 'object') return;
      if (Array.isArray(node)) {
        for (var i = 0; i < node.length; i++) walk(node[i], keyHint, depth + 1);
        return;
      }
      // Cloudinary returns both; keep only the https one.
      if (isHttpUrl(node.secure_url)) {
        add(node.secure_url);
      }
      for (var k in node) {
        if (!Object.prototype.hasOwnProperty.call(node, k)) continue;
        if (k === 'url' && isHttpUrl(node.secure_url)) continue;
        walk(node[k], UPLOAD_URL_KEY_RE.test(k), depth + 1);
      }
    }
    walk(data, false, 0);
    return out;
  }

  function recordUploadResponse(rawText, requestUrl) {
    if (!rawText) return;
    var data;
    try { data = JSON.parse(rawText); } catch (_) { return; }
    var base = null;
    try { base = requestUrl ? new URL(String(requestUrl), location.href).href : null; } catch (_) { }
    var urls = extractUploadUrls(data, base);
    if (!urls.length) return;

    var changed = false;
    for (var i = 0; i < urls.length; i++) {
      if (uploadedImageUrls.indexOf(urls[i]) === -1) {
        uploadedImageUrls.push(urls[i]);
        changed = true;
      }
    }
    if (!changed) return;

    // Mirror the running list as a form_input so abandoned-form recovery
    // shows the uploaded URLs even if the user never submits.
    var joined = uploadedImageUrls.join(',');
    push('form_input', {
      element_name: 'uploadImagesURL',
      form_field_type: 'text',
      meta: { value: joined, length: joined.length },
    });
  }

  // ---------- lead-submission interception ----------
  // Most lead forms here never let the browser submit: their JS validates,
  // POSTs to the signage.com dashboard API and only shows "thank you" when
  // that call succeeds. The submit event / button click fires even when
  // validation fails, so for these forms the request is the signal — the
  // fields are read from its body (the exact payload the lead system got,
  // including values that live outside the <form>, e.g. sign type) and the
  // submission is recorded once the response confirms it. Each `accept`
  // mirrors the success check the theme itself applies to that endpoint.
  var LEAD_ENDPOINTS = [
    {
      re: /dashboard\.signage\.com\/api\/submit\/quicksignage(?:[/?#]|$)/i,
      label: 'Quick Signage Lead',
      // Validation failures come back as { status: 422, errors }, sometimes on HTTP 200.
      accept: function (status, data) {
        return isOkStatus(status) && !!data && data.status !== 422 && data.success !== false;
      },
    },
    {
      re: /dashboard\.signage\.com\/api\/save\/contact(?:[/?#]|$)/i,
      label: 'Contact Form',
      accept: function (status, data) { return isOkStatus(status) && !!data && data.status === 'success'; },
    },
    {
      re: /dashboard\.signage\.com\/api\/submit\/partnerships(?:[/?#]|$)/i,
      label: 'Partnership Request',
      accept: function (status, data) { return isOkStatus(status) && !!data; },
    },
    {
      re: /dashboard\.signage\.com\/api\/leads(?:[/?#]|$)/i,
      label: 'Lead',
      accept: function (status, data) { return isOkStatus(status) && !!data; },
    },
  ];
  // Bulky plumbing the lead APIs take that adds nothing to a submission record.
  var LEAD_FIELD_EXCLUDE = ['tracking', 'tracking_metadata', 'sign_types_data'];
  // How long after a submit / submit-button click a lead request is still
  // attributed to that form. Covers the awaits pages do before POSTing
  // (IP-geo lookup, image upload).
  var SUBMIT_INTENT_WINDOW_MS = 60 * 1000;
  var lastSubmitIntent = null;

  function noteSubmitIntent(root) {
    lastSubmitIntent = { root: root, at: Date.now() };
  }

  function isOkStatus(s) { return s >= 200 && s < 300; }

  function matchLeadEndpoint(url, method) {
    if (!url || String(method || 'GET').toUpperCase() !== 'POST') return null;
    var s = String(url);
    for (var i = 0; i < LEAD_ENDPOINTS.length; i++) {
      if (LEAD_ENDPOINTS[i].re.test(s)) return LEAD_ENDPOINTS[i];
    }
    return null;
  }

  function addField(fields, key, val) {
    if (fields[key] === undefined) {
      fields[key] = val;
    } else {
      if (!Array.isArray(fields[key])) fields[key] = [fields[key]];
      fields[key].push(val);
    }
  }

  function fileMeta(v) {
    if (typeof Blob === 'undefined' || !(v instanceof Blob)) return null;
    return { filename: v.name || null, mime_type: v.type || null, size_bytes: v.size };
  }

  // Request body → plain fields object. Handles FormData, URLSearchParams,
  // JSON and urlencoded strings; anything else (Blob, stream) → null.
  function requestBodyFields(body) {
    if (body == null) return null;
    var fields = {};
    try {
      if ((typeof FormData !== 'undefined' && body instanceof FormData) ||
        (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)) {
        body.forEach(function (v, k) { addField(fields, k, fileMeta(v) || v); });
      } else if (typeof body === 'string') {
        var t = body.trim();
        if (t.charAt(0) === '{') {
          var o = JSON.parse(t);
          if (!o || typeof o !== 'object' || Array.isArray(o)) return null;
          for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) fields[k] = o[k];
        } else if (t.indexOf('=') !== -1) {
          return requestBodyFields(new URLSearchParams(t));
        } else {
          return null;
        }
      } else {
        return null;
      }
    } catch (_) { return null; }
    for (var x = 0; x < LEAD_FIELD_EXCLUDE.length; x++) delete fields[LEAD_FIELD_EXCLUDE[x]];
    return fields;
  }

  // Runs synchronously as the request starts, so the body, the form it
  // belongs to and the current pageview are captured before the page moves
  // on (several forms pushState to ?thanks-for-response right after).
  function beginLeadRequest(endpoint, body) {
    var intent = lastSubmitIntent;
    var fresh = intent && (Date.now() - intent.at) <= SUBMIT_INTENT_WINDOW_MS;
    return {
      endpoint: endpoint,
      fields: requestBodyFields(body),
      root: fresh ? intent.root : null,
      pageViewUuid: pageViewUuid,
    };
  }

  function finishLeadRequest(req, status, rawText) {
    var data = null;
    try { data = rawText ? JSON.parse(rawText) : null; } catch (_) { }
    if (!req.endpoint.accept(status, data)) return;

    var root = req.root;
    // A data-clarity-manual form records itself via Clarity.captureForm().
    if (root && isManualForm(root)) return;

    var fields = req.fields || (root ? serializeForm(root) : {});
    if (uploadedImageUrls.length && fields.uploadImagesURL === undefined) {
      fields.uploadImagesURL = uploadedImageUrls.join(',');
    }
    // Explicit markup wins, then the lead's own page label ("Main USA",
    // "Collection Page", "Backlit"), then the form's name/id, then the endpoint.
    var name = (root && (root.getAttribute('data-track-name') || root.getAttribute('data-clarity-form'))) ||
      pickField(fields, ['lead_page_Source', 'lead_page', 'table_type']) ||
      (root && (root.getAttribute('name') || root.getAttribute('id'))) ||
      req.endpoint.label;

    recordSubmission(name, root ? formSelectorFor(root) : null, fields, true, req.pageViewUuid);
    // The lead request is the record; a parked native snapshot of the same
    // form (contact page: API call, then form.submit()) must not double it.
    clearNativeSubmit();
  }

  function xhrText(xhr) {
    if (xhr.responseType === '' || xhr.responseType === 'text') return xhr.responseText;
    if (xhr.responseType === 'json') return xhr.response == null ? null : JSON.stringify(xhr.response);
    return null;
  }

  // Patch fetch — wrap, never mutate. Only touches upload and lead endpoints.
  (function patchFetch() {
    if (typeof window.fetch !== 'function') return;
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = null;
      var method = null;
      try {
        url = typeof input === 'string' ? input : (input && (input.url || input.href || String(input)));
        method = (init && init.method) || (input && typeof input === 'object' && input.method) || 'GET';
      } catch (_) { }
      var watchUpload = isUploadEndpoint(url);
      var leadReq = null;
      try {
        var lead = matchLeadEndpoint(url, method);
        if (lead) leadReq = beginLeadRequest(lead, init && init.body);
      } catch (_) { }
      var p;
      try { p = origFetch.apply(this, arguments); }
      catch (e) { return Promise.reject(e); }
      if ((!watchUpload && !leadReq) || !p || typeof p.then !== 'function') return p;
      return p.then(function (res) {
        try {
          if (res && typeof res.clone === 'function') {
            var status = res.status;
            res.clone().text()
              .then(function (t) {
                if (watchUpload) { try { recordUploadResponse(t, url); } catch (_) { } }
                if (leadReq) { try { finishLeadRequest(leadReq, status, t); } catch (_) { } }
              })
              .catch(function () { });
          }
        } catch (_) { }
        return res;
      });
    };
  })();

  // Patch XMLHttpRequest — same principle, wrap and observe only upload and lead endpoints.
  (function patchXHR() {
    if (typeof XMLHttpRequest === 'undefined') return;
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        this.__clarityUpload = isUploadEndpoint(url);
        this.__clarityUrl = url;
        this.__clarityLead = matchLeadEndpoint(url, method);
      } catch (_) { }
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      var xhr = this;
      var leadReq = null;
      try { if (xhr.__clarityLead) leadReq = beginLeadRequest(xhr.__clarityLead, body); } catch (_) { }
      if (xhr.__clarityUpload || leadReq) {
        try {
          xhr.addEventListener('load', function () {
            try {
              if (xhr.__clarityUpload && xhr.status >= 200 && xhr.status < 300) {
                recordUploadResponse(xhrText(xhr), xhr.__clarityUrl);
              }
            } catch (_) { }
            try { if (leadReq) finishLeadRequest(leadReq, xhr.status, xhrText(xhr)); } catch (_) { }
          });
        } catch (_) { }
      }
      return origSend.apply(this, arguments);
    };
  })();

  // ---------- form auto-capture ----------
  // Matches native submit buttons AND common "fake submit" buttons that use
  // type="button" + a custom click handler (e.g. Signize's .submitButton,
  // signs.inc's .qf-submit / .cbf-qf-submit which POST via fetch instead of
  // firing a native submit event).
  var SUBMIT_BTN_SELECTOR =
    'button[type="submit"], input[type="submit"], input[type="image"], ' +
    '.submitButton, .qf-submit, .cbf-qf-submit, [data-clarity-submit]';

  // Site pattern: buttons/cards that toggle a `.sel` (or `.cbf-sel`) class
  // inside a container tagged with `data-name`. Used by the quote form's
  // Indoor/Outdoor toggles and mockup-type picker.
  var SITE_TOGGLE_SELECTOR = '.qf-toggle-btn, .cbf-qf-toggle-btn';
  var SITE_CARD_SELECTOR = '.qf-mockup-card, .cbf-qf-mockup-card';
  function isSelectedNode(el) {
    return !!(el && el.classList && (el.classList.contains('sel') || el.classList.contains('cbf-sel')));
  }
  // A "form root" is a real <form> or any container tagged data-clarity-form
  // (lead forms built from loose inputs, e.g. collection-with-form).
  var FORM_ROOT_SELECTOR = 'form, [data-clarity-form]';
  // Site-chrome forms that are not leads (header search, cart, localization).
  var IGNORED_FORM_ACTION_RE = /\/(search|cart|localization|account)(\/|\?|#|$)/i;

  function isIgnoredForm(root) {
    if (!root || !root.getAttribute) return true;
    if (root.getAttribute('data-clarity-ignore') !== null) return true;
    if (root.getAttribute('role') === 'search') return true;
    var action = root.tagName === 'FORM' ? (root.getAttribute('action') || '') : '';
    return IGNORED_FORM_ACTION_RE.test(action);
  }

  // data-clarity-manual: the page JS validates + submits via fetch and calls
  // Clarity.captureForm() on confirmed success, so auto-capture must not also
  // record a submission (it would fire on failed validation too).
  function isManualForm(root) {
    return !!(root && root.getAttribute && root.getAttribute('data-clarity-manual') !== null);
  }

  function formControlsOf(root) {
    if (root.tagName === 'FORM' && root.elements) return root.elements;
    return root.querySelectorAll('input, select, textarea');
  }

  // Per-form dedupe window: 2 s covers "click fires + custom JS calls form.submit()".
  var recentlySubmitted = new WeakMap();

  // `name` is what a form submits under, but several lead forms here leave it
  // off (signage-v4/v5 inputs carry only a placeholder). Fall back to the id,
  // then a slug of the placeholder / aria-label ("Email Address" →
  // "email_address") so field events and snapshots still say which field
  // they came from.
  function slugify(v) {
    return String(v || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64);
  }

  function fieldNameOf(el) {
    if (!el) return null;
    if (el.name) return el.name;
    if (el.id) return el.id;
    var label = (el.getAttribute && (el.getAttribute('placeholder') || el.getAttribute('aria-label'))) || '';
    var slug = slugify(label);
    if (slug) return slug;
    // JS-built file inputs (collection-with-form appends a bare
    // <input type="file"> into .upload-area) carry none of the above. Name
    // them after the nearest classed/id'd container — not its text, which the
    // page swaps for the chosen filename before this runs.
    if (el.type === 'file') {
      var p = el.parentElement;
      for (var d = 0; p && d < 3; d++, p = p.parentElement) {
        var token = p.id || (safeClassName(p) || '').split(/\s+/)[0];
        if (token) return slugify(token) || 'file';
      }
      return 'file';
    }
    return null;
  }

  function serializeForm(form) {
    var fields = {};
    try {
      var els = formControlsOf(form) || [];
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        if (!el) continue;
        if (el.disabled) continue;
        if (el.type === 'password') continue;
        if (el.getAttribute && el.getAttribute('data-clarity-ignore') !== null) continue;

        var type = el.type;
        var tag = el.tagName;
        // Unnamed buttons / fieldsets carry no value worth a fallback key.
        if (!el.name && (tag === 'BUTTON' || tag === 'FIELDSET' || /^(button|submit|reset|image)$/.test(type))) continue;
        var name = fieldNameOf(el);
        if (!name) continue;

        if (type === 'checkbox' || type === 'radio') {
          if (!el.checked) continue;
          addField(fields, name, (el.value != null && el.value !== '') ? el.value : true);
        } else if (type === 'file') {
          var files = [];
          var fl = el.files || [];
          for (var j = 0; j < fl.length; j++) {
            files.push({ filename: fl[j].name, mime_type: fl[j].type, size_bytes: fl[j].size });
          }
          if (files.length === 1) fields[name] = files[0];
          else if (files.length > 1) fields[name] = files;
        } else if (tag === 'SELECT' && el.multiple) {
          var opts = [];
          var so = el.selectedOptions || [];
          for (var k = 0; k < so.length; k++) opts.push(so[k].value);
          fields[name] = opts;
        } else {
          fields[name] = el.value;
        }
      }
    } catch (_) { }

    // Site pattern: containers marked with `data-name` hold buttons/cards
    // that mark selection via `.sel` / `.cbf-sel` (Indoor/Outdoor toggle,
    // mockup-type picker). There's no matching <input>, so serializeForm
    // would otherwise miss these values entirely.
    try {
      var selectionGroups = form.querySelectorAll('[data-name]');
      for (var g = 0; g < selectionGroups.length; g++) {
        var groupEl = selectionGroups[g];
        var groupName = groupEl.getAttribute('data-name');
        if (!groupName || fields[groupName] !== undefined) continue;
        var selectedNodes = groupEl.querySelectorAll('.sel, .cbf-sel');
        if (!selectedNodes.length) continue;
        var vals = [];
        for (var s = 0; s < selectedNodes.length; s++) {
          var v = selectedNodes[s].dataset && selectedNodes[s].dataset.val;
          if (v) vals.push(v);
        }
        if (vals.length === 1) fields[groupName] = vals[0];
        else if (vals.length > 1) fields[groupName] = vals;
      }
    } catch (_) { }

    // Attach any image URLs captured from Cloudinary / imagescloud uploads
    // as a comma-separated string.
    if (uploadedImageUrls.length && fields.uploadImagesURL === undefined) {
      fields.uploadImagesURL = uploadedImageUrls.join(',');
    }

    return fields;
  }

  // getAttribute, never form.name / form.id: an <input name="name"> (or
  // name="id") inside the form shadows those properties with the element.
  function formNameFor(form) {
    return form.getAttribute('data-track-name') ||
      form.getAttribute('data-clarity-form') ||
      form.getAttribute('name') ||
      form.getAttribute('id') ||
      'form';
  }

  function formSelectorFor(form) {
    var id = form.getAttribute('id');
    if (id) return '#' + id;
    var n = form.getAttribute('name');
    if (n) return 'form[name="' + n + '"]';
    var t = form.getAttribute('data-track-name');
    return t ? '[data-track-name="' + t + '"]' : null;
  }

  function pickField(fields, keys, fuzzyRegex) {
    for (var i = 0; i < keys.length; i++) {
      var v = fields[keys[i]];
      if (typeof v === 'string' && v.length) return v;
    }
    if (fuzzyRegex) {
      for (var k in fields) {
        if (!Object.prototype.hasOwnProperty.call(fields, k)) continue;
        if (fuzzyRegex.test(k) && typeof fields[k] === 'string' && fields[k].length) return fields[k];
      }
    }
    return null;
  }

  // opts.name overrides the derived form name; opts.fields is merged over the
  // serialized fields (a null value removes that key).
  function doFormSubmit(form, wasComplete, opts) {
    opts = opts || {};
    if (!form || isBlocked || pageBlocked) return;
    if (!sessionUuid || !pageViewUuid) return;

    var now = Date.now();
    var last = recentlySubmitted.get(form);
    if (last && (now - last) < 2000) return; // dedupe submit-vs-click double fire
    recentlySubmitted.set(form, now);

    var fields = serializeForm(form);
    if (opts.fields) {
      for (var fk in opts.fields) {
        if (!Object.prototype.hasOwnProperty.call(opts.fields, fk)) continue;
        if (opts.fields[fk] === null || opts.fields[fk] === undefined) delete fields[fk];
        else fields[fk] = opts.fields[fk];
      }
    }
    recordSubmission(opts.name || formNameFor(form), formSelectorFor(form), fields, wasComplete);
  }

  // ---------- cancelled-then-native submits (Shopify forms) ----------
  // A cancelled submit on a POST form is parked in session storage. On the
  // next page load it's recorded if Shopify's success marker is in the URL
  // (`?contact_posted=true` / `?customer_posted=true`), and discarded
  // otherwise — so failed captchas / validation never count. Shopify's
  // `/challenge` page sits between the post and the redirect, so it keeps
  // the snapshot alive.
  var NATIVE_SUBMIT_KEY = 'clarity_pending_native_submit';
  var NATIVE_SUBMIT_TTL_MS = 30 * 60 * 1000;
  var NATIVE_SUCCESS_RE = /[?&](contact_posted|customer_posted)=true(&|$)/i;

  function rememberNativeSubmit(form) {
    if (!sessionUuid || !pageViewUuid) return;
    if (String(form.getAttribute('method') || '').toLowerCase() !== 'post') return;
    ssSet(NATIVE_SUBMIT_KEY, JSON.stringify({
      name: formNameFor(form),
      selector: formSelectorFor(form),
      fields: serializeForm(form),
      session_uuid: sessionUuid,
      page_view_uuid: pageViewUuid,
      at: Date.now(),
    }));
  }

  function clearNativeSubmit() { ssRemove(NATIVE_SUBMIT_KEY); }

  // Called once the pageview is open on each page load.
  function settleNativeSubmit() {
    var raw = ssGet(NATIVE_SUBMIT_KEY);
    if (!raw) return;
    if (/^\/challenge(\/|$)/.test(location.pathname)) return;
    clearNativeSubmit();
    var rec = null;
    try { rec = JSON.parse(raw); } catch (_) { }
    if (!rec || !rec.at || (Date.now() - rec.at) > NATIVE_SUBMIT_TTL_MS) return;
    if (!NATIVE_SUCCESS_RE.test(location.search)) return;
    // Pin to the pageview the form was submitted on when it's the same session.
    var pv = rec.session_uuid === sessionUuid ? rec.page_view_uuid : null;
    recordSubmission(rec.name, rec.selector, rec.fields || {}, true, pv);
  }

  // Shared tail of every recorded submission: activity event, /form-submit,
  // /identify when an email is present, then a beacon flush. pvUuid pins the
  // record to the pageview the submission started on (a lead request can
  // resolve after the page has already pushState-navigated).
  function recordSubmission(name, selector, fields, wasComplete, pvUuid) {
    if (isBlocked || pageBlocked) return;
    var pv = pvUuid || pageViewUuid;
    if (!sessionUuid || !pv) return;

    push('form_submit_success', {
      meta: { form_name: name, form_selector: selector },
    });

    post('/form-submit', {
      session_uuid: sessionUuid,
      page_view_uuid: pv,
      form_name: name,
      form_selector: selector,
      fields: fields,
      was_complete: wasComplete !== false,
      submitted_at: nowIso(),
    }, true);

    var email = pickField(fields, ['email', 'Email', 'EMAIL', 'user_email', 'contact_email', 'email_address'], /email/i);
    if (email) {
      post('/identify', {
        session_uuid: sessionUuid,
        email: email,
        // Fuzzy fallbacks cover Shopify's bracketed names, e.g. contact[Name].
        name: pickField(fields, ['name', 'your_name', 'full_name', 'fullName', 'first_name', 'firstName'],
          /(^|\[)(full_?name|your_?name|name)\]?$/i),
        phone: pickField(fields, ['phone', 'phone_number', 'phoneNumber', 'tel', 'mobile', 'contact_number'],
          /(^|\[|_)(phone|tel|mobile)/i),
        meta: null,
      }, true);
    }

    // Flush any pending events with beacon so they survive a full-page nav after submit.
    flush(true);

    // Reset upload accumulator so a subsequent submission on the same page
    // doesn't carry stale URLs.
    uploadedImageUrls = [];
  }

  function trackClick(e) {
    var raw = e.target;
    if (!raw || raw.nodeType !== 1) return;
    // Toggles/selects have their own handler.
    if (raw.closest && raw.closest('[data-toggle],[data-select],' + SITE_TOGGLE_SELECTOR + ',' + SITE_CARD_SELECTOR)) return;

    var el = (raw.closest && raw.closest('a, button, [data-track], [role="button"], [role="link"], input[type="submit"], input[type="button"]')) || raw;
    var text = (el.innerText || el.textContent || '').trim().slice(0, 512) || null;
    var href = el.href || null;

    push('click', {
      element_tag: (el.tagName || '').toLowerCase() || null,
      element_id: el.id || null,
      element_classes: safeClassName(el),
      element_text: text,
      element_href: href,
      viewport_x: e.clientX,
      viewport_y: e.clientY,
      meta: { x: e.clientX, y: e.clientY, href: href },
    });
  }

  function trackToggleOrSelect(e) {
    var t = e.target;
    if (!t || !t.closest) return;

    // Site pattern: .qf-toggle-btn / .cbf-qf-toggle-btn inside a container
    // with `data-name`. Class flips to `.sel` / `.cbf-sel` on click.
    var siteToggle = t.closest(SITE_TOGGLE_SELECTOR);
    if (siteToggle) {
      var siteToggleGroup = siteToggle.closest('[data-name]');
      setTimeout(function () {
        var on = isSelectedNode(siteToggle);
        push('toggle', {
          element_id: siteToggle.id || null,
          element_tag: (siteToggle.tagName || '').toLowerCase(),
          meta: {
            group: siteToggleGroup ? siteToggleGroup.getAttribute('data-name') : null,
            value: (siteToggle.dataset && siteToggle.dataset.val) || null,
            state: on ? 'on' : 'off',
          },
        });
      }, 0);
      return;
    }

    // Site pattern: mockup cards — multi-select via `.sel` / `.cbf-sel`.
    var siteCard = t.closest(SITE_CARD_SELECTOR);
    if (siteCard) {
      var siteCardGroup = siteCard.closest('[data-name]');
      setTimeout(function () {
        var selected = isSelectedNode(siteCard);
        push(selected ? 'select' : 'deselect', {
          element_id: siteCard.id || null,
          element_tag: (siteCard.tagName || '').toLowerCase(),
          meta: {
            group: siteCardGroup ? siteCardGroup.getAttribute('data-name') : null,
            value: (siteCard.dataset && siteCard.dataset.val) || null,
            selected: selected,
          },
        });
      }, 0);
      return;
    }

    var toggle = t.closest('[data-toggle]');
    if (toggle) {
      // Read state on next tick so consumers can flip classes/aria first.
      setTimeout(function () {
        var on = toggle.classList.contains('is-active') ||
          toggle.getAttribute('aria-pressed') === 'true';
        push('toggle', {
          element_id: toggle.id || null,
          element_tag: toggle.tagName.toLowerCase(),
          meta: {
            group: toggle.dataset.toggle,
            value: toggle.dataset.value || null,
            state: on ? 'on' : 'off',
          },
        });
      }, 0);
      return;
    }

    var sel = t.closest('[data-select]');
    if (sel) {
      setTimeout(function () {
        var selected = sel.classList.contains('is-selected') ||
          sel.getAttribute('aria-selected') === 'true';
        push(selected ? 'select' : 'deselect', {
          element_id: sel.id || null,
          element_tag: sel.tagName.toLowerCase(),
          meta: {
            group: sel.dataset.select,
            value: sel.dataset.value || null,
            selected: selected,
          },
        });
      }, 0);
    }
  }

  function isTrackableField(el) {
    if (!el || !el.matches) return false;
    if (!el.matches('input, textarea, select')) return false;
    if (el.type === 'password') return false;
    if (el.getAttribute && el.getAttribute('data-clarity-ignore') !== null) return false;
    return true;
  }

  function attachListeners() {
    if (listenersAttached) return;
    listenersAttached = true;

    // Scroll milestones (25/50/75/100 fired once per page view).
    var milestones = [25, 50, 75, 100];
    var fired = new Set();
    window.addEventListener('scroll', function () {
      var depth = getScrollDepth();
      if (depth > maxScroll) maxScroll = depth;
      for (var i = 0; i < milestones.length; i++) {
        var m = milestones[i];
        if (depth >= m && !fired.has(m)) {
          fired.add(m);
          push('scroll', { scroll_depth: m, meta: { depth: m } });
        }
      }
    }, { passive: true });

    // Reset scroll milestones on SPA route change.
    window.addEventListener('clarity:pageview-open', function () { fired.clear(); });

    // Clicks (capture so we still see cancelled clicks).
    document.addEventListener('click', trackClick, true);

    // Toggles / multi-selects.
    document.addEventListener('click', trackToggleOrSelect);

    // Real form submits — fires for <button type="submit">, <input type="submit">,
    // and Enter-key submits. Passive: we never preventDefault, so form behaviour
    // is untouched. Whether the browser really submits is only known once the
    // page's own handlers have run: if one calls preventDefault() the page is
    // taking over (validate + fetch), and the submission is recorded when its
    // lead request succeeds (LEAD_ENDPOINTS) — not here, where it would also
    // count submits that fail validation. The decision runs from a bubbling
    // window listener (after the page's handlers), with a timeout fallback
    // for handlers that stop propagation.
    var pendingSubmit = null;
    var settleSubmit = function (evt) {
      var p = pendingSubmit;
      if (!p || p.event !== evt) return;
      pendingSubmit = null;
      if (evt.defaultPrevented) {
        // Cancelled — but the page may still post it natively later via
        // form.submit() (which fires no submit event), e.g. Shopify's
        // storefront bot-protection on {% form 'contact' %}. Park a snapshot;
        // it's recorded only if the next page confirms success.
        try { rememberNativeSubmit(p.form); } catch (_) { }
        return;
      }
      try { doFormSubmit(p.form, true); } catch (_) { }
    };
    document.addEventListener('submit', function (e) {
      if (isBlocked || pageBlocked) return;
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      if (isIgnoredForm(form)) return;
      noteSubmitIntent(form);
      if (isManualForm(form)) return;
      pendingSubmit = { event: e, form: form };
      setTimeout(function () { settleSubmit(e); }, 0);
    }, true);
    window.addEventListener('submit', settleSubmit);

    // Submit-button clicks — covers forms whose "submit" button is
    // type="button" with a custom JS handler (e.g. lead-form's .submitButton),
    // and type="submit" buttons whose click handler cancels the submit
    // (signage-v5). Emits form_submit_attempt for every submit-looking click
    // and marks the form as the owner of the next lead request; the
    // submission itself is recorded when that request succeeds, or by the
    // submit listener above for a native submit.
    document.addEventListener('click', function (e) {
      if (isBlocked || pageBlocked) return;
      var raw = e.target;
      if (!raw || !raw.closest) return;
      var btn = raw.closest(SUBMIT_BTN_SELECTOR);
      if (!btn) return;
      var form = btn.closest(FORM_ROOT_SELECTOR);
      if (!form || isIgnoredForm(form)) return;

      try {
        noteSubmitIntent(form);
        push('form_submit_attempt', {
          element_id: btn.id || null,
          element_tag: (btn.tagName || '').toLowerCase(),
          element_classes: safeClassName(btn),
          element_text: ((btn.innerText || btn.textContent || '').trim().slice(0, 512)) || null,
          meta: {
            form_name: formNameFor(form),
            form_selector: formSelectorFor(form),
          },
        });
      } catch (_) { }
    }, true);

    // Form fields.
    var debounceMap = new WeakMap();

    document.addEventListener('focusin', function (e) {
      var el = e.target;
      if (!isTrackableField(el)) return;
      push('form_focus', {
        element_id: el.id || null,
        element_name: fieldNameOf(el),
        form_field_type: el.type || 'text',
      });
    });

    document.addEventListener('focusout', function (e) {
      var el = e.target;
      if (!isTrackableField(el)) return;
      var filled = !!(el.value && String(el.value).length);
      push('form_blur', {
        element_id: el.id || null,
        element_name: fieldNameOf(el),
        form_field_type: el.type || 'text',
        meta: { filled: filled },
      });
    });

    document.addEventListener('input', function (e) {
      var el = e.target;
      if (!isTrackableField(el)) return;
      var t = debounceMap.get(el);
      if (t) clearTimeout(t);
      debounceMap.set(el, setTimeout(function () {
        var rawVal = (el.type === 'checkbox' || el.type === 'radio') ? el.checked : el.value;
        var strVal = rawVal === null || rawVal === undefined ? '' : String(rawVal);
        push('form_input', {
          element_id: el.id || null,
          element_name: fieldNameOf(el),
          form_field_type: el.type || 'text',
          meta: { value: strVal, length: strVal.length },
        });
      }, 500));
    });

    // File uploads.
    document.addEventListener('change', function (e) {
      var el = e.target;
      if (!el || el.tagName !== 'INPUT' || el.type !== 'file') return;
      var f = el.files && el.files[0];
      if (!f) return;

      if (f.size > FILE_MAX_BYTES) {
        push('file_error', {
          element_id: el.id || null,
          element_name: fieldNameOf(el),
          meta: { filename: f.name, reason: 'size_over_limit', size_bytes: f.size },
        });
        return;
      }
      var extMatch = /\.([a-z0-9]+)$/i.exec(f.name || '');
      var ext = extMatch ? extMatch[1].toLowerCase() : '';
      var mimeOk = !f.type || OK_FILE_MIMES.indexOf(f.type) !== -1;
      var extOk = ext && OK_FILE_EXTS.indexOf(ext) !== -1;
      if (!mimeOk && !extOk) {
        push('file_error', {
          element_id: el.id || null,
          element_name: fieldNameOf(el),
          meta: { filename: f.name, reason: 'invalid_type', size_bytes: f.size },
        });
        return;
      }
      push('file_upload', {
        element_id: el.id || null,
        element_name: fieldNameOf(el),
        meta: { filename: f.name, mime_type: f.type, size_bytes: f.size },
      });
    });

    // Clipboard.
    document.addEventListener('copy', function () {
      var length = 0;
      try {
        var s = document.getSelection && document.getSelection().toString();
        length = s ? s.length : 0;
      } catch (_) { }
      push('copy', { meta: { length: length } });
    });

    // Resize (debounced).
    var resizeT;
    window.addEventListener('resize', function () {
      clearTimeout(resizeT);
      resizeT = setTimeout(function () {
        push('resize', { meta: { width: window.innerWidth, height: window.innerHeight } });
      }, 300);
    });

    // Visibility. Engaged timer must pause the instant the tab hides — the
    // idle threshold alone would otherwise credit up to 30 s of phantom
    // engagement to a backgrounded tab.
    document.addEventListener('visibilitychange', function () {
      push('visibility_change', { meta: { state: document.hidden ? 'hidden' : 'visible' } });
      if (document.hidden) {
        heartbeatBeacon(); // pauses + flushes pending delta via keepalive fetch
        flush();
      }
      // On return-to-visible we deliberately don't auto-resume — a fresh
      // input event will (see onInputEvent above).
    });

    // Page exit → flush engagement first, then close the pageview. Both use
    // keepalive fetch so they survive unload.
    window.addEventListener('pagehide', function () {
      heartbeatBeacon();
      closePageView('close', true);
    });

    // Engaged-time input tracking. capture:true so we still see events that
    // a downstream handler stops propagation on; passive:true so we never
    // interfere with the page's own scroll/touch behaviour.
    for (var iIdx = 0; iIdx < INPUT_EVENTS.length; iIdx++) {
      document.addEventListener(INPUT_EVENTS[iIdx], onInputEvent, { passive: true, capture: true });
    }
    // Treat page load itself as an implicit input — if the tab is visible
    // right now, start accruing immediately rather than waiting for the
    // first mouse twitch.
    if (document.visibilityState === 'visible') {
      lastInputTs = perfNow();
      lastEngagedTickMs = lastInputTs;
    }

    // Idle-timeout recycle. Recycle *before* pushing the activity so the
    // event lands on the fresh session.
    var recycleIfStale = function () {
      var last = readLastActivity();
      var lastMs = last ? Date.parse(last) : 0;
      if (lastMs && (Date.now() - lastMs) > SESSION_TIMEOUT_MS) {
        clearSessionId();
        sessionUuid = null;
        boot();
      } else {
        touch();
      }
    };
    ['mousedown', 'keydown', 'touchstart'].forEach(function (t) {
      document.addEventListener(t, recycleIfStale, { passive: true });
    });

    // SPA route change: patch history + popstate.
    patchHistory();
  }

  function closePageView(exitType, useBeacon) {
    if (isBlocked || !pageViewUuid) return Promise.resolve();
    var timeOnPage = Math.floor((Date.now() - pageEnteredAtMs) / 1000);
    // Flush events first so they land on this page view.
    var flushP = flush(useBeacon);
    var closeP = post('/pageview/close', {
      session_uuid: sessionUuid,
      page_view_uuid: pageViewUuid,
      left_at: nowIso(),
      time_on_page_seconds: timeOnPage,
      max_scroll_depth: maxScroll,
      exit_type: exitType,
    }, useBeacon).catch(function () { });
    return Promise.all([flushP, closeP]);
  }

  // ---------- SPA ----------
  var spaLock = false;
  var booting = false;
  function spaRouteChange() {
    // While boot() is still opening the first pageview, a URL rewrite (e.g.
    // theme.liquid stripping ?handle=) must not open a second session/pageview.
    if (isBlocked || spaLock || booting) return Promise.resolve();
    spaLock = true;
    var prev = pageViewUuid;
    var timeOnPage = Math.floor((Date.now() - pageEnteredAtMs) / 1000);

    // Flush pending engagement so it lands on the closing pageview rather
    // than leaking into whichever pageview opens next.
    if (prev) heartbeatBeacon();

    var closeP = prev
      ? flush().then(function () {
        return post('/pageview/close', {
          session_uuid: sessionUuid,
          page_view_uuid: prev,
          left_at: nowIso(),
          time_on_page_seconds: timeOnPage,
          max_scroll_depth: maxScroll,
          exit_type: 'navigation',
        }).catch(function () { });
      })
      : Promise.resolve();

    return closeP
      .then(function () {
        if (prev) previousPageViewUuid = prev;
        pageViewUuid = null;

        // Recompute soft block for the new URL. Skip opening a pageview
        // when navigating into a blocked path; resume on the next nav out.
        pageBlocked = isPathBlocked(location.pathname);
        if (pageBlocked) return null;

        // If we booted on a blocked page, the session was never started.
        if (!sessionUuid) {
          return startSession(null).then(function () {
            if (!sessionUuid) return null;
            return openPageView();
          });
        }
        return openPageView();
      })
      .then(function () {
        if (!pageBlocked && pageViewUuid) {
          try { window.dispatchEvent(new Event('clarity:pageview-open')); } catch (_) { }
        }
      })
      .catch(function () { })
      .then(function () { spaLock = false; });
  }

  // Only a change of path or query is a navigation. Hash-only / same-URL
  // rewrites (scroll-to-anchor helpers, state replacement) are ignored.
  function routeKey() { return location.pathname + location.search; }
  var lastRouteKey = routeKey();
  function onHistoryChange() {
    var k = routeKey();
    if (k === lastRouteKey) return;
    lastRouteKey = k;
    spaRouteChange();
  }

  function patchHistory() {
    var origPush = history.pushState;
    var origReplace = history.replaceState;
    history.pushState = function () {
      var r = origPush.apply(this, arguments);
      Promise.resolve().then(onHistoryChange);
      return r;
    };
    history.replaceState = function () {
      var r = origReplace.apply(this, arguments);
      Promise.resolve().then(onHistoryChange);
      return r;
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  // ---------- boot ----------
  function boot() {
    if (isBlocked) return Promise.resolve();

    pageBlocked = isPathBlocked(location.pathname);

    // Attach listeners + intervals unconditionally so that if the visitor
    // SPA-navigates from a blocked page to a tracked page, we can resume.
    attachListeners();
    if (!intervalsStarted) {
      intervalsStarted = true;
      setInterval(function () { flush(); }, FLUSH_INTERVAL_MS);
      setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);
    }

    if (pageBlocked) return Promise.resolve();

    booting = true;
    return fetchGeoWithTimeout()
      .catch(function () { return null; })
      .then(function (geo) {
        if (geo && geo.country && BLOCKED_COUNTRIES.indexOf(geo.country) !== -1) {
          isBlocked = true;
          return null;
        }
        if (sessionUuid) return null;
        return startSession(geo).catch(function () { });
      })
      .then(function () {
        if (isBlocked || pageBlocked || !sessionUuid) return null;
        return openPageView().catch(function () { });
      })
      .then(function () {
        if (isBlocked || pageBlocked || !pageViewUuid) return;
        try { window.dispatchEvent(new Event('clarity:pageview-open')); } catch (_) { }
        try { settleNativeSubmit(); } catch (_) { }
      })
      .catch(function () { })
      .then(function () {
        booting = false;
        lastRouteKey = routeKey();
      });
  }

  // ---------- public API ----------
  window.Clarity = {
    track: function (type, data) { push(type, data); },
    identify: function (email, name, phone, meta) {
      if (isBlocked || !sessionUuid) return Promise.resolve(null);
      return post('/identify', {
        session_uuid: sessionUuid,
        email: email,
        name: name || null,
        phone: phone || null,
        meta: meta || null,
      });
    },
    formSubmit: function (opts) {
      opts = opts || {};
      if (isBlocked || !sessionUuid || !pageViewUuid) return Promise.resolve(null);
      return post('/form-submit', {
        session_uuid: sessionUuid,
        page_view_uuid: pageViewUuid,
        form_name: opts.name,
        form_selector: opts.selector || null,
        fields: opts.fields || {},
        was_complete: opts.was_complete !== false,
        submitted_at: nowIso(),
      });
    },
    captureForm: function (root, opts) {
      try {
        if (typeof root === 'string') root = document.querySelector(root);
        if (!root || root.nodeType !== 1) return;
        opts = opts || {};
        doFormSubmit(root, opts.was_complete !== false, opts);
      } catch (_) { }
    },
    flush: function () { return flush(); },
    spaRouteChange: spaRouteChange,
    getSessionUuid: function () { return sessionUuid; },
    getPageViewUuid: function () { return pageViewUuid; },
    getVisitorUuid: function () { return visitorUuid; },
  };

  boot();
})();
